// RelationshipWorkspace. Mirrors data-component="relationship-workspace"
// from the approved HTML. Owns the current/former filter bar, team
// note, the list of RelationshipCard rows, and the Former empty state.
//
// Filtering happens here so each RelationshipCard stays a pure
// presenter. "Add relationship" lives in the sibling header above.
//
// TODO(runtime): hydrate rows from services/relationships.list once
// the HTTP endpoint lands.
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { RelationshipCard } from './RelationshipCard.js';
import { FormerRelationshipsEmpty } from './FormerRelationshipsEmpty.js';
import type { Relationship, RelationshipStatus } from '../mockData.js';

export interface RelationshipWorkspaceProps {
  relationships: Relationship[];
  linkedHorseName: string;
  onEdit: (relationship: Relationship) => void;
  onEnd: (relationship: Relationship) => void;
  filter: RelationshipStatus;
  onFilterChange: (filter: RelationshipStatus) => void;
}

export function RelationshipWorkspace({
  relationships,
  linkedHorseName,
  onEdit,
  onEnd,
  filter,
  onFilterChange: setFilter,
}: RelationshipWorkspaceProps): ReactNode {

  const currentCount = useMemo(
    () => relationships.filter((r) => r.status === 'current').length,
    [relationships],
  );
  const formerCount = useMemo(
    () => relationships.filter((r) => r.status === 'former').length,
    [relationships],
  );
  const visible = useMemo(
    () => relationships.filter((r) => r.status === filter),
    [relationships, filter],
  );

  return (
    <div className="relationship-main" data-component="relationship-workspace">
      <div className="relationship-filter">
        <div role="group" aria-label="Relationship status">
          <button
            type="button"
            data-filter="current"
            aria-pressed={filter === 'current'}
            onClick={() => setFilter('current')}
          >
            Current <span>{currentCount}</span>
          </button>
          <button
            type="button"
            data-filter="former"
            aria-pressed={filter === 'former'}
            onClick={() => setFilter('former')}
          >
            Former <span>{formerCount}</span>
          </button>
        </div>
        <span>
          Linked horse: <strong>{linkedHorseName}</strong>
        </span>
      </div>
      <p className="team-note">
        One horse, multiple specialists. Add each veterinarian or shoer
        separately, with their role. Adding someone does not replace an
        existing connection.
      </p>
      <div>
        {visible.length > 0 ? (
          visible.map((relationship) => (
            <RelationshipCard
              key={relationship.id}
              relationship={relationship}
              linkedHorseName={linkedHorseName}
              onEdit={onEdit}
              onEnd={onEnd}
            />
          ))
        ) : filter === 'former' ? (
          <FormerRelationshipsEmpty />
        ) : (
          <div className="former-empty"><h3>No current connections recorded</h3><p>Add a connection after confirming it with the customer.</p></div>
        )}
      </div>
    </div>
  );
}
